<template>
<slot name="empty" v-if="loaded && items.length === 0"></slot>

<div v-if="!loaded || loadingList" className="inline ? '' : 'page-wrapper'" class="position-relative">
    <div class="progress progress-sm position-absolute w-100">
        <div class="progress-bar progress-bar-indeterminate"></div>
    </div>
</div>

<div :className="inline ? '' : 'page-wrapper'" v-show="loaded && !($slots.empty && items.length === 0)">
    <div :className="inline ? '' : 'container-fluid'">
        <context-menu
            ref="menu"
            location="bottom right"
            :items="itemMenuItems">
        </context-menu>

        <confirm title="Are you sure that you want to remove this item?" ref="confirm-delete"></confirm>
        <prompt ref="create" title="Create new item"></prompt>
        <component v-if="createItemComponent" :is="createItemComponent" v-bind="createItemComponentProps" ref="create-item-component"></component>

        <slot></slot>

        <div v-if="!inline" class="page-header d-print-none">
            <div class="container-xl">
                <div class="row align-items-center">
                    <div class="col">
                        <h2 class="page-title">
                            {{ title }}
                        </h2>
                    </div>
                    <div class="col-auto ms-auto d-print-none" v-if="createItemComponent || apiCreate">
                        <div class="btn-list">
                            <slot name="create"></slot>
                            <div class="btn btn-primary d-none d-sm-inline-block" @click="create">
                                <icon icon="plus" height="1.25em" /> Add new
                            </div>
                            <div class="btn btn-primary d-sm-none btn-icon" @click="create">
                                <icon icon="plus" height="1.25em" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div :className="inline ? '' : 'page-body'">
            <div :className="inline ? '' : 'container-xl'">
                <div class="card">
                    <div class="card-body border-bottom py-3">
                        <div class="d-flex">
                            <div class="text-muted">
                                <div class="d-inline-block">
                                    <input type="text" class="form-control" placeholder="Search" v-model="searchQuery" />
                                </div>
                            </div>
                            <div class="ms-auto text-muted">
                                <div class="ms-2">
                                    <button v-if="apiCreateFolder" class="btn btn-outline-primary me-3" @click="openNewFolderModal">
                                        New folder
                                    </button>
                                    <slot name="top"></slot>
                                </div>
                            </div>
                        </div>
                    </div>
                    <nav v-if="useFolders" aria-label="breadcrumb" style="padding: 20px;">
                        <ol class="breadcrumb">
                            <li class="breadcrumb-item" :class="{ 'active': !currentFolder }" :aria-current="!currentFolder ? 'page' : null">
                                <span class="d-inline-flex justify-content-center align-items-center" style="height: 22px;" v-if="!currentFolder">Home</span>
                                <button class="btn btn-link p-0" v-else @click="currentFolder = ''">Home</button>
                            </li>
                            <li v-for="(folder, i) in (currentFolder.split('/')[0] === '' ? [] : currentFolder.split('/'))" :key="i" class="breadcrumb-item" :class="{ 'active': isActiveBreadcrumbFolder(i) }" :aria-current="isActiveBreadcrumbFolder(i) ? 'page' : null">
                                <span class="d-inline-flex justify-content-center align-items-center" style="height: 22px;" v-if="isActiveBreadcrumbFolder(i)">{{ folder }}</span>
                                <button class="btn btn-link p-0" v-else @click="currentFolder = currentFolder.split('/').slice(0, i + 1).join('/')">{{ folder }}</button>
                            </li>
                        </ol>
                    </nav>
                    <div class="table-responsive mb-0">
                        <table class="table card-table table-vcenter text-nowrap datatable" :class="{ 'table-hover': !gridView && filteredItems.length > 0 }">
                            <thead>
                            <tr>
                                <th class="w-1" v-if="showId">ID</th>
                                <th v-for="(column, index) in columns" :key="index" @click="sort(column)" :class="{ 'cursor-pointer': column.sortable }">
                                    {{ column.title }}
                                    <icon v-if="column.sortable && sortBy === column.name" :icon="sortDesc ? 'arrow-down' : 'arrow-up'" class="ms-1" />
                                </th>
                                <th class="text-end">
                                    <button @click="gridView = !gridView" class="btn btn-icon btn-outline-muted">
                                        <icon :icon="gridView ? 'list' : 'grid'" style="pointer-events: none" />
                                    </button>
                                </th>
                            </tr>
                            </thead>
                            <tbody v-if="gridView">
                                <tr>
                                    <td colspan="100">
                                        <div v-if="useFolders ? (currentFolderFolders.length > 0 || currentFolderItems.length > 0) : filteredItems.length > 0" class="row row-cards">
                                            <template v-if="useFolders">
                                                <div class="col-md-4 col-lg-3" @click="currentFolder = currentFolder ? currentFolder + '/' + folder.name : folder.name" v-for="(folder, i) in currentFolderFolders" :key="i">
                                                    <div class="card">
                                                        <div class="card-img-top">
                                                            <icon icon="folder" height="128" class="text-muted" />
                                                        </div>
                                                        <div class="card-body d-flex align-items-center justify-content-between">
                                                            <p class="card-text text-truncate mb-0 me-2">{{ folder.name }}</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </template>
                                            <div v-for="(item, i) in (useFolders ? currentFolderItems : filteredItems)" :key="i" class="col-md-4 col-lg-3">
                                                <div class="card" :class="{ 'card-active': selectedItem && item.id === selectedItem.id }" @click="selectItem(item)">
                                                    <img v-if="item.type === 'image' || item.thumbnail_url" :src="item.download_url && item.thumbnail_url" class="card-img-top" />
                                                    <div v-else class="card-img-top">
                                                        <icon icon="file" height="128" class="text-muted" />
                                                    </div>
                                                    <div class="card-body d-flex align-items-center justify-content-between">
                                                        <p class="card-text text-truncate mb-0 me-2">{{ item.name }}</p>
                                                        <button v-if="(menuItems && menuItems.length) || apiDelete" class="btn btn-icon btn-sm btn-outline-primary" @click="$refs.menu.open($event, item)">
                                                            <icon icon="menu"></icon>
                                                        </button>
                                                        <slot name="row-end" v-bind:item="item"></slot>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div v-else class="text-center text-muted">No entries</div>
                                    </td>
                                </tr>
                            </tbody>
                            <tbody v-else>
                            <tr v-if="useFolders ? (currentFolderFolders.length === 0 && currentFolderItems.length === 0) : filteredItems.length === 0">
                                <td colspan="100" class="text-center text-muted">No entries</td>
                            </tr>
                            <template v-if="useFolders">
                                <tr v-for="(folder, i) in currentFolderFolders" :key="i">
                                    <td colspan="100" @click="currentFolder = currentFolder ? currentFolder + '/' + folder.name : folder.name">
                                        <div class="d-flex align-items-center w-100">
                                            <icon icon="folder" class="me-2"></icon>
                                            {{ folder.name }}
                                        </div>
                                    </td>
                                </tr>
                            </template>
                            <tr v-for="item in (useFolders ? currentFolderItems : filteredItems)" :key="item.id" :class="{ 'table-active': selectedItem && item.id === selectedItem.id }" @click="selectItem(item)">
                                <td v-if="showId">
                                    <span class="text-muted">{{ item.id }}</span>
                                </td>
                                <td v-for="(column, index) in columns" :key="index" :style="{ 'width': column.width ? column.width + 'px' : (column.type =='boolean' ? '50px' : 'auto') }">
                                    <icon v-if="column.icon" :icon="column.icon" class="me-2 text-secondary" :height="20"></icon>

                                    <span v-if="column.type === 'datetime'">
                                            {{ $filters.formatDate(item[column.name]*1000, 'long-compact', true) }}
                                        </span>
                                    <span v-else-if="column.type === 'date'">
                                            {{ $filters.formatDate(item[column.name]*1000, 'long', false) }}
                                        </span>
                                    <div v-else-if="column.type === 'boolean'" class="text-center">
                                        <span v-if="item[column.name] && item[column.name] != '0'"><span class="badge bg-success me-1"></span></span>
                                        <span v-else><span class="badge bg-danger me-1"></span></span>
                                    </div>
                                    <span v-else-if="column.type === 'custom'">
                                            <slot name="custom" v-bind:item="item" v-bind:column="column"></slot>
                                        </span>
                                    <div v-else-if="column.type === 'reference'">
                                        <router-link :to="{ 'name': column.reference_name, 'query': { 'id': item[column.reference_column] } }">
                                            {{ item[column.name] }}
                                        </router-link>
                                    </div>
                                    <span v-else>
                                        {{ item[column.name] }}
                                    </span>
                                </td>
                                <td class="text-end" style="width: 50px;">
                                    <button v-if="(menuItems && menuItems.length) || apiDelete" class="btn btn-icon btn-outline-primary" @click="$refs.menu.open($event, item)">
                                        <icon icon="menu"></icon>
                                    </button>
                                    <slot name="row-end" v-bind:item="item"></slot>
                                </td>
                            </tr>
                            </tbody>
                        </table>
                    </div>
                    <div class="card-footer d-flex align-items-center">
                        <button v-if="apiPaging && hasMore" @click="loadMore()" class="btn btn-primary">Load More</button>
                        <p class="m-0 text-muted ms-auto" v-if="count">Total <span>{{ count }}</span> entries</p>
                        <slot name="actions" :selected-item="selectedItem" :current-folder="currentFolder"></slot>
                    </div>
                </div>
            </div>
        </div>

        <modal v-if="useFolders" ref="new-folder-modal" width="320px" @closed="newFolderName = ''">
            <div class="modal-body border-top">
                <label class="form-label">Folder name</label>
                <input ref="new-folder-name-input" class="form-control" v-model="newFolderName" :disabled="newFolderLoading" />
            </div>
            <div class="modal-footer">
                <button class="btn btn-primary" @click="createFolder" :disabled="newFolderLoading">Create</button>
            </div>
        </modal>

        <modal ref="rename-item-modal" width="320px" @closed="onRenameItemModalClose">
            <div class="modal-body border-top">
                <label class="form-label">New Name</label>
                <input ref="rename-new-name-input" class="form-control" v-model="renameNewName" :disabled="renameLoading" />
            </div>
            <div class="modal-footer">
                <button class="btn btn-primary" @click="renameItem" :disabled="renameLoading">Rename</button>
            </div>
        </modal>
    </div>
</div>
</template>

<script>
import ContextMenu from "@/components/ContextMenu.vue";
import Confirm from "@/components/Confirm.vue";
import Prompt from "@/components/Prompt.vue";

export default {
    components: { Prompt, Confirm, ContextMenu },
    props: {
        title: {
            type: String,
            required: true
        },
        apiSearch: {
            type: Boolean,
            required: false,
            default: false
        },
        apiPaging: {
            type: Boolean,
            required: false,
            default: false
        },
        apiList: {
            type: Function,
            required: true
        },
        apiDelete: {
            type: Function,
            required: false
        },
        apiCreateFolder: {
            type: Function,
            required: false
        },
        apiRename: {
            type: Function,
            required: false
        },
        apiCreate: {
            type: Function,
            required: false
        },
        menuItems: {
            type: Array,
            required: false,
            default: () => {
                return [];
            }
        },
        columns: {
            type: Array,
            required: false,
            default: () => {
                return [
                    { title: "Title", name: "title" }
                ];
            }
        },
        useFolders: {
            type: Boolean,
            default: false
        },
        createItemComponent: {
            type: Object,
            required: false
        },
        createItemComponentProps: {
            type: Object,
            required: false
        },
        showId: {
            type: Boolean,
            required: false,
            default: true
        },
        defaultView: {
            type: String,
            required: false,
            default: "list"
        },
        filterFunc: {
            type: Function,
            required: false,
            default: (items) => items
        },
        inline: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    data() {
        return {
            gridView: false,
            loadingList: false,
            loadingListTimeout: null,
            loaded: false,
            hasMore: false,
            folders: [],
            items: [],
            currentFolder: "",
            filteredItems: [],
            searchQuery: "",
            currentRouteName: "",
            count: 0,
            itemMenuItems: [],
            sortBy: null,
            sortDesc: false,

            selectedItem: null,
            selectedItemForRename: null,

            newFolderLoading: false,
            newFolderName: "",

            renameLoading: false,
            renameNewName: ""
        };
    },
    created() {
        this.load();
        this.currentRouteName = this.$route.name;
        this.gridView = this.defaultView === "grid";

        // add existing items
        this.menuItems.forEach(m => {
            this.itemMenuItems.push(m);
        });

        if (this.apiRename) {
            this.itemMenuItems.push({ title: "Rename", callback: this.openRenameItemModal });
        }

        if (this.apiDelete) {
            this.itemMenuItems.push({ title: "Delete", callback: this.deleteItem, class: "text-danger" });
        }
    },
    watch: {
        searchQuery(val) {
            if (this.apiSearch) {
                this.load();
            } else {
                const compareVal = val.toLocaleLowerCase();
                this.filteredItems = this.items.filter(item => {
                    const json = JSON.stringify(item);
                    return json.toLocaleLowerCase().indexOf(compareVal) > -1;
                });
            }
        }
    },
    computed: {
        currentFolderFolders() {
            const folderTree = {};

            function addToFolderTree(tree, path) {
                const parts = path.split("/");
                let current = tree;

                parts.forEach((part, index) => {
                    if (!current[part]) {
                        current[part] = { subfolders: {} };
                    }
                    if (index < parts.length - 1) {
                        current = current[part].subfolders;
                    }
                });
            }

            // Add folders from items paths (implicit folders)
            this.items.forEach(item => {
                const folderPath = item.folder || ""; // Empty string for top-level items
                addToFolderTree(folderTree, folderPath);
            });

            // Add explicit folders from this.folders
            this.folders.forEach(folder => {
                const folderPath = folder.folder || ""; // Empty string for top-level folders
                addToFolderTree(folderTree, folderPath);
            });

            function getSubfolders(folder, pathParts) {
                let current = folder;

                for (const part of pathParts) {
                    if (part && current[part]) {
                        current = current[part].subfolders;
                    }
                }

                return Object.keys(current).filter(name => name !== "").map(name => ({
                    name
                }));
            }

            const pathParts = (this.currentFolder || "").split("/").filter(Boolean);

            return getSubfolders(folderTree, pathParts);
        },
        currentFolderItems() {
            return this.filteredItems.filter(item => item.folder === this.currentFolder);
        }
    },
    methods: {
        openRenameItemModal(item) {
            this.selectedItemForRename = item;
            this.renameNewName = item.name;
            this.$refs["rename-item-modal"].open();
            this.$nextTick(() => {
                this.$refs["rename-new-name-input"].focus();
            });
        },
        onRenameItemModalClose() {
            this.selectedItemForRename = null;
            this.renameNewName = "";
        },
        renameItem() {
            this.renameLoading = true;
            this.apiRename(this.selectedItemForRename.id, this.renameNewName, this.selectedItemForRename.folder).then(() => {
                this.load(true);
                this.$refs["rename-item-modal"].close();
            }).catch(e => {
                this.flashError(e);
            }).finally(() => {
                this.renameLoading = false;
            });
        },
        isActiveBreadcrumbFolder(index) {
            return index === this.currentFolder.split("/").length - 1;
        },
        openNewFolderModal() {
            this.$refs["new-folder-modal"].open();
            this.$nextTick(() => {
                this.$refs["new-folder-name-input"].focus();
            });
        },
        createFolder() {
            this.newFolderLoading = true;
            this.apiCreateFolder(this.currentFolder ? (this.currentFolder + "/" + this.newFolderName) : this.newFolderName).then(() => {
                this.load(true);
                this.$refs["new-folder-modal"].close();
            }).catch(e => {
                this.flashError(e);
            }).finally(() => {
                this.newFolderLoading = false;
            });
        },
        selectItem(item) {
            this.selectedItem = item;
        },
        defferedLoad() {
            const beforeId = 0;
            const search = this.searchQuery;

            if (this.loadingList) {
                return;
            }
            this.loadingList = true;

            this.apiList(beforeId, search).then(data => {
                this.$emit("load", data);
                if (this.useFolders) {
                    this.folders = JSON.parse(JSON.stringify(data.folders));
                }
                this.items = JSON.parse(JSON.stringify(data.items));

                // console.log(data.items);
                // console.log(data.items.filter(this.filterFunc));
                this.filteredItems = JSON.parse(JSON.stringify(data.items.filter(this.filterFunc)));
                this.count = data.count;
                this.hasMore = data.has_more;
            }).catch(e => {
                this.flashError(e);
            }).finally(() => {
                this.loadingList = false;
                this.loaded = true;
            });
        },
        load() {
            if (this.loadingListTimeout) {
                window.clearTimeout(this.loadingListTimeout);
            }
            this.loadingListTimeout = window.setTimeout(this.defferedLoad, 250);
        },
        loadMore() {
            if (this.items.length < 1) {
                return;
            }

            const beforeId = this.items[this.items.length - 1].id;
            const search = this.searchQuery;

            this.apiList(beforeId, search).then(data => {
                data.items.forEach(i => {
                    this.items.push(i);
                });
                this.count = data.count;
                this.hasMore = data.has_more;
            }).catch(e => {
                this.flashError(e);
            });
        },
        invalidateItem(id, item) {
            const update = this.items.find(i => i.id === id);
            Object.assign(update, item);
        },
        sort(column) {
            if (this.sortBy === column.name) {
                if (this.sortDesc) {
                    // Reset sorting if already descending
                    this.sortBy = null;
                    this.sortDesc = false;
                    this.filteredItems = JSON.parse(JSON.stringify(this.items));
                } else {
                    // Set to descending if currently ascending
                    this.sortDesc = true;
                }
            } else {
                // Set the column as the new sorting column and start with ascending
                this.sortBy = column.name;
                this.sortDesc = false;
            }

            // Apply sorting to filteredItems only if sorting is active
            if (this.sortBy) {
                this.filteredItems.sort((a, b) => {
                    const aValue = a[column.name];
                    const bValue = b[column.name];

                    if (aValue === bValue) return 0;
                    if (this.sortDesc) {
                        return aValue < bValue ? 1 : -1;
                    }
                    return aValue > bValue ? 1 : -1;
                });
            }
        },
        deleteItem(item) {
            this.$refs["confirm-delete"].open(() => {
                this.apiDelete(item.id).then(data => {
                    this.load();
                }).catch(e => {
                    this.flashError(e);
                });
            });
        },
        create() {
            if (this.createItemComponent) {
                this.$refs["create-item-component"].open((_, close) => {
                    close();
                    this.load();
                });
                return;
            }

            this.$refs.create.open("", (_, close) => {
                close();
            });
        }
    }
};
</script>

<style scoped>
.card-img-top {
    height: 200px;
    background-color: #f0f0f0;
    display: flex;
    align-items: center;
    justify-content: center;
    object-fit: cover;
}
</style>
